import React from "react";
import { Link } from "react-router-dom";
import { ExternalLink, BookOpen, Award, Building2 } from "lucide-react";

export default function About() {
    return (
        <div data-testid="about-page">
            {/* Hero */}
            <section className="relative">
                <div className="mx-auto max-w-6xl px-6 md:px-12 py-28 grid md:grid-cols-12 gap-12 items-center">
                    <div className="md:col-span-7">
                        <div className="label-tag">Meet Kevin Bradshaw</div>
                        <h1 className="mt-4 font-display text-5xl md:text-6xl font-black leading-[1.02] tracking-tight">
                            25 years on the line.
                            <br />
                            <span className="gold-shimmer">A lifetime in the fight.</span>
                        </h1>
                        <p className="mt-8 text-white/80 text-lg leading-relaxed">
                            I'm <span className="text-gold-500 font-semibold">Kevin Bradshaw</span>,
                            President of the Memphis &amp; West Tennessee Central Labor Council,
                            coordinating 50+ affiliated local unions across the region. For two
                            and a half decades I've served as a member, steward, officer, and
                            bargainer — on picket lines, at arbitration tables, and in city
                            halls.
                        </p>
                        <p className="mt-5 text-white/70 leading-relaxed">
                            Bradshaw Solutions is the consulting practice I built to bring that
                            experience directly to locals that can't afford a six-figure law firm
                            but deserve the same caliber of representation. Every engagement is
                            hands-on, flat-fee, and union-side only.
                        </p>
                        <div className="mt-10 grid grid-cols-3 gap-6">
                            {[
                                ["25+", "Years in the movement"],
                                ["50+", "Locals coordinated"],
                                ["100%", "Union-side, always"],
                            ].map(([k, v]) => (
                                <div key={v} className="border-l-2 border-gold-500/60 pl-4">
                                    <div className="font-display text-3xl text-gold-500 font-bold">{k}</div>
                                    <div className="text-xs tracking-[0.2em] uppercase text-white/60 mt-1">{v}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="md:col-span-5">
                        <div className="relative">
                            <div className="absolute -inset-4 rounded-2xl bg-gold-500/10 blur-2xl" />
                            <img
                                src="https://customer-assets.emergentagent.com/job_worker-advocate-3/artifacts/axbzyq4l_KDP_Cover_6x9_1875x2775.jpeg"
                                alt="Struggle & Solidarity — Labor Strikes in Memphis (1968–Present) by Kevin Bradshaw"
                                className="relative rounded-2xl border border-gold-500/30 w-full object-contain bg-ink-900 h-[560px]"
                            />
                            <div className="absolute bottom-4 left-4 right-4 bg-ink-900/90 backdrop-blur-sm border border-gold-500/30 rounded-xl p-4">
                                <div className="label-tag">Memphis, Tennessee</div>
                                <div className="mt-1 font-display text-lg text-white">
                                    From "I AM A MAN" to today —
                                    <br />the fight continues.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Story */}
            <section className="bg-ink-800 border-y border-white/10 py-24">
                <div className="mx-auto max-w-4xl px-6 md:px-12">
                    <div className="label-tag">The work</div>
                    <h2 className="mt-4 font-display text-4xl md:text-5xl font-bold leading-tight">
                        Memphis labor runs through my bones.
                    </h2>
                    <div className="mt-10 space-y-6 text-white/80 text-lg leading-relaxed font-sans">
                        <p>
                            My labor education began where a whole nation's did — on the streets
                            of Memphis in the shadow of the 1968 Sanitation Strike. The signs
                            read <em>I AM A MAN</em>. The lesson was that dignity on the job is
                            never given. It is won, line by line, contract by contract, and it is
                            defended every day after.
                        </p>
                        <p>
                            I've spent my career honoring that lesson: on the Kellogg's fight, in
                            steward rooms teaching Weingarten rights, at the arbitration table
                            defending terminations that should never have happened, and in the
                            coalition work that built <span className="text-gold-500 font-semibold">C.O.P.P.E.R.</span>
                            — the Coalition of Public Political Education &amp; Representation.
                        </p>
                        <p>
                            As President of the Memphis &amp; West Tennessee Central Labor Council,
                            I coordinate the political, bargaining, and solidarity work of more
                            than 50 affiliates — from sanitation and trades to teachers and
                            healthcare. Under our watch the Council secured the{" "}
                            <span className="text-gold-500 font-semibold">$150,000 District 10 Labor Education &amp; Trades Pathways</span> grant
                            to connect 120+ underserved residents to union careers.
                        </p>
                        <p>
                            Now, through Bradshaw Solutions, that experience is available to
                            your local — whether you're a 20-member shop facing your first
                            grievance or an international prepping a 10,000-worker strike.
                        </p>
                    </div>
                </div>
            </section>

            {/* Milestones */}
            <section className="py-24">
                <div className="mx-auto max-w-6xl px-6 md:px-12">
                    <div className="label-tag">Beyond the consulting</div>
                    <h2 className="mt-4 font-display text-4xl md:text-5xl font-bold">
                        Three ways I keep building the movement.
                    </h2>
                    <div className="mt-14 grid md:grid-cols-3 gap-6">
                        <MilestoneCard
                            icon={BookOpen}
                            tag="The Book"
                            title="Struggle &amp; Solidarity"
                            body="Labor Strikes in Memphis (1968–Present) — the story of Memphis labor told by someone who lived it. Free to read, free to share."
                            cta="Read the eBook"
                            href="https://laboreducation.org/book"
                        />
                        <MilestoneCard
                            icon={Award}
                            tag="The App"
                            title="LaborEducation.org"
                            body="Free steward training with 6 certification levels &amp; 31 modules — Foundation through Trainer Track, plus Grievance Masterclass and Contract Negotiations. Endorsed by Memphis-Shelby County Schools (100K+ students)."
                            cta="Explore the platform"
                            href="https://laboreducation.org"
                            featured
                        />
                        <MilestoneCard
                            icon={Building2}
                            tag="The Council"
                            title="Memphis &amp; W. TN CLC"
                            body="50+ affiliated locals. Coalition-building, political action, and solidarity infrastructure for the entire region."
                            cta="About the Council"
                            href="https://laboreducation.org/unions/union_memphis_clc"
                        />
                    </div>
                </div>
            </section>

            {/* Principles */}
            <section className="py-20 bg-ink-800 border-y border-white/10">
                <div className="mx-auto max-w-4xl px-6 md:px-12 text-center">
                    <div className="heraldic-divider mb-6">
                        <span className="text-xs tracking-[0.3em] uppercase">Principles</span>
                    </div>
                    <div className="grid md:grid-cols-3 gap-8 mt-10 text-left">
                        {[
                            { t: "Union-side, only.", d: "I never consult for management. Period." },
                            { t: "Flat fees, written.", d: "No surprise bills. No billable-hour games. Your local knows the cost before we start." },
                            { t: "Members first.", d: "Every decision runs through one test: does this serve the rank and file?" },
                        ].map((p) => (
                            <div key={p.t} className="p-8 bg-ink-900 border border-white/10 rounded-2xl">
                                <h3 className="font-display text-xl font-bold text-gold-500">{p.t}</h3>
                                <p className="mt-3 text-white/70 leading-relaxed">{p.d}</p>
                            </div>
                        ))}
                    </div>
                    <Link
                        to="/contact"
                        data-testid="about-contact-cta"
                        className="mt-12 inline-block px-7 py-4 rounded-full bg-gold-500 text-ink-900 font-bold uppercase tracking-wider text-sm hover:bg-gold-400"
                    >
                        Start the Conversation
                    </Link>
                </div>
            </section>
        </div>
    );
}

function MilestoneCard({ icon: Icon, tag, title, body, cta, href, featured }) {
    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`milestone-${tag.toLowerCase().replace(/\s+/g, "-")}`}
            className={`group relative block rounded-2xl p-8 border transition-all ${
                featured
                    ? "bg-gold-500/5 border-gold-500/50 hover:border-gold-500"
                    : "bg-ink-800 border-white/10 gold-glow"
            }`}
        >
            <Icon size={28} className="text-gold-500" />
            <div className="mt-5 label-tag">{tag}</div>
            <h3
                className="mt-2 font-display text-2xl font-bold leading-tight"
                dangerouslySetInnerHTML={{ __html: title }}
            />
            <p className="mt-3 text-white/70 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: body }} />
            <div className="mt-6 inline-flex items-center gap-2 text-gold-500 text-xs uppercase tracking-[0.2em] font-semibold">
                {cta} <ExternalLink size={13} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </div>
        </a>
    );
}
